import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { onyxImageUrl } from '../../config/onyxEmotions';
import { ONYX_OPEN_AFTER_LOAD_KEY } from '../../lib/onyxOpenFlag';
import { ONYX_FAB_SIZE } from '../../config/onyxWakeSequence';
import { useAuth } from '../../hooks/useAuth';
import {
  THEME_PRESELECT_EVENT,
  isThemePreselectPath,
  isThemePreselectVisible,
} from '../../lib/themePreselect';

const HelpChatWidget = lazy(() =>
  import('./HelpChatWidget').then((m) => ({ default: m.HelpChatWidget }))
);

/**
 * Light FAB gate for INP: keep framer/gsap/rive/TTS out of the critical path
 * until idle or first tap. Static sleep WebP (~30 KiB) is enough for closed state.
 * Hidden on theme preselection (Hospitality / Entertainment / Office).
 */
export const HelpChatFabGate: React.FC = () => {
  const { user } = useAuth();
  const [preselect, setPreselect] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isThemePreselectVisible()) return true;
    return isThemePreselectPath() && !user;
  });
  const [loadWidget, setLoadWidget] = useState(false);

  useEffect(() => {
    const syncFromEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ visible?: boolean }>).detail;
      if (typeof detail?.visible === 'boolean') {
        setPreselect(detail.visible);
        return;
      }
      setPreselect(isThemePreselectVisible());
    };
    const syncFromPath = () => {
      if (isThemePreselectVisible()) {
        setPreselect(true);
        return;
      }
      setPreselect(isThemePreselectPath(window.location.pathname) && !user);
    };

    window.addEventListener(THEME_PRESELECT_EVENT, syncFromEvent);
    window.addEventListener('popstate', syncFromPath);
    syncFromPath();
    return () => {
      window.removeEventListener(THEME_PRESELECT_EVENT, syncFromEvent);
      window.removeEventListener('popstate', syncFromPath);
    };
  }, [user]);

  useEffect(() => {
    if (preselect) {
      setLoadWidget(false);
      return;
    }

    // After window load + idle — never compete with LCP (mobile CWV)
    const enable = () => setLoadWidget(true);
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let loadTimer: number | undefined;

    const schedule = () => {
      if (typeof w.requestIdleCallback === 'function') {
        idleId = w.requestIdleCallback(enable, { timeout: 8000 });
      } else {
        timeoutId = window.setTimeout(enable, 5000);
      }
    };

    if (document.readyState === 'complete') {
      loadTimer = window.setTimeout(schedule, 1500);
    } else {
      const onLoad = () => {
        loadTimer = window.setTimeout(schedule, 1500);
      };
      window.addEventListener('load', onLoad, { once: true });
      return () => {
        window.removeEventListener('load', onLoad);
        if (loadTimer !== undefined) window.clearTimeout(loadTimer);
        if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      };
    }

    return () => {
      if (loadTimer !== undefined) window.clearTimeout(loadTimer);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [preselect]);

  const onFabTap = useCallback(() => {
    try {
      sessionStorage.setItem(ONYX_OPEN_AFTER_LOAD_KEY, '1');
    } catch {
      /* ignore */
    }
    setLoadWidget(true);
  }, []);

  if (preselect) {
    return null;
  }

  if (loadWidget) {
    return (
      <Suspense fallback={null}>
        <HelpChatWidget />
      </Suspense>
    );
  }

  return (
    <button
      type="button"
      onClick={onFabTap}
      onPointerEnter={() => setLoadWidget(true)}
      aria-label="Open help chat"
      className="fixed z-[60] bottom-4 right-4 sm:bottom-6 sm:right-6 rounded-full overflow-visible bg-transparent border-0 p-0 cursor-pointer"
      style={{ width: ONYX_FAB_SIZE, height: ONYX_FAB_SIZE }}
    >
      <img
        src={onyxImageUrl('SLEEPING')}
        alt=""
        width={ONYX_FAB_SIZE}
        height={ONYX_FAB_SIZE}
        className="w-full h-full object-contain pointer-events-none"
        decoding="async"
        loading="lazy"
      />
    </button>
  );
};
